import React from 'react';
import styled from 'styled-components';
import { observer } from 'mobx-react';
import { NAMESPACES } from '~/i18n/namespaces';
import { useTranslation } from 'react-i18next';
import useAnalytics from '~/common/useAnalytics';
import colors from '@honeybook/hbui/dist/styles/colors';
import HBButton from '@honeybook/hbui/dist/components/HBButton/HBButton';
import { absoluteAll, flexColumn } from '@honeybook/hbui/dist/styles/mixins';
import { transition } from '@honeybook/hbui/dist/styles/animations';
import { useCopyToClipboard } from '~/common/hooks/useCopyToClipboard';
import { IFlowTemplate, LeadFormStatus } from '~/types/model.types';

const LeadFormCardActionOverlay = ({
    flow,
    goToFlow
}: {
    flow: IFlowTemplate;
    goToFlow: () => void;
}) => {
    const { copyText, isCopied } = useCopyToClipboard({ timeout: 2000 });

    const { t } = useTranslation(NAMESPACES.LEAD_FORMS, {
        keyPrefix: 'lead_capture_screen'
    });

    const { track } = useAnalytics();

    const copyFlowUrl = e => {
        e.stopPropagation();
        track('copy lead form link', {
            action: 'click',
            flowId: flow._id,
            source: 'flow_card'
        });
        copyText(flow.flow_url);
    };

    const onEditForm = () => {
        track('go to template', {
            action: 'click',
            type: 'flow',
            template_name: flow.title
        });
        goToFlow();
    };

    const showCopyLinkButton = flow.public_flow_status === LeadFormStatus.Live;

    return (
        <ActionsOverlay>
            <StyledButton
                size="xs"
                buttonTheme="secondary-translucent"
                onClick={onEditForm}
                testIdName="edit-form"
            >
                {t('edit_form')}
            </StyledButton>
            {showCopyLinkButton && (
                <StyledButton
                    size="xs"
                    buttonTheme="secondary-solid-primary"
                    onClick={copyFlowUrl}
                    testIdName="copy-link"
                >
                    {isCopied ? t('copied') : t('copy_link')}
                </StyledButton>
            )}
        </ActionsOverlay>
    );
};

export default observer(LeadFormCardActionOverlay);

// -------------------------------- Styles -------------------------------- //

export const ActionsOverlay = styled.div`
    ${absoluteAll};
    ${flexColumn};
    background: ${colors.black_03};
    backdrop-filter: blur(2px);
    justify-content: center;
    align-items: center;
    gap: 8px;
    opacity: 0;
    ${transition('opacity')};
`;

// this is to prevent the width jump when the button text changes
const StyledButton = styled(HBButton)`
    width: 90px;
`;
